import React, { useState } from 'react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { Settings2, Loader2, Save } from 'lucide-react';

const authHeader = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function ClaSettingsDialog() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/cla-settings`, { headers: authHeader(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to load');
      setRows((Array.isArray(d) ? d : []).map(r => ({ ...r })));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openDlg = () => { setOpen(true); load(); };
  const setRow = (id, patch) => setRows(rs => rs.map(r => (r.entity_id === id ? { ...r, ...patch } : r)));

  const save = async (r) => {
    setSavingId(r.entity_id);
    try {
      const res = await fetch(`${API_URL}/api/payroll/cla-settings/${r.entity_id}`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ enabled: !!r.enabled, teaching: Number(r.teaching) || 0, non_teaching: Number(r.non_teaching) || 0 }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      toast.success(`CLA saved for ${r.name}`);
      setRow(r.entity_id, { configured: true });
    } catch (e) { toast.error(e.message); } finally { setSavingId(null); }
  };

  const reset = async (r) => {
    setSavingId(r.entity_id);
    try {
      const res = await fetch(`${API_URL}/api/payroll/cla-settings/${r.entity_id}`, {
        method: 'DELETE', headers: authHeader(), credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Reset failed');
      toast.success(`CLA reset to default for ${r.name}`);
      setRow(r.entity_id, { enabled: true, teaching: 800, non_teaching: 400, configured: false });
    } catch (e) { toast.error(e.message); } finally { setSavingId(null); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={openDlg} data-testid="cla-settings-btn">
        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> CLA Settings
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cla-settings-dialog">
          <DialogHeader>
            <DialogTitle>CLA settings by institute</DialogTitle>
            <DialogDescription>
              Decide CLA per institute. Default is ON — Teaching ₹800 / Non-Teaching ₹400. Turn it off for
              institutes that don't give CLA, or change the amounts. Applies to payroll you generate from now on.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 py-12 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-slate-400 py-10 text-sm">No institutes in your scope.</div>
          ) : (
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-2 text-[11px] uppercase tracking-wide text-slate-400">
                <span>Institute</span><span className="w-16 text-center">CLA on</span>
                <span className="w-24 text-center">Teaching</span><span className="w-24 text-center">Non-Teach.</span><span className="w-16" />
              </div>
              {rows.map(r => (
                <div key={r.entity_id} className="grid sm:grid-cols-[1fr_auto_auto_auto_auto] grid-cols-2 gap-3 items-center border rounded-lg bg-white p-2.5" data-testid={`cla-row-${r.entity_id}`}>
                  <div className="col-span-2 sm:col-span-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                    {r.configured
                      ? <button onClick={() => reset(r)} className="text-[10px] text-indigo-600 hover:underline" data-testid={`cla-reset-${r.entity_id}`}>Reset to default</button>
                      : <span className="text-[10px] text-slate-400">using default</span>}
                  </div>
                  <div className="flex items-center justify-center sm:w-16">
                    <Switch checked={!!r.enabled} onCheckedChange={(v) => setRow(r.entity_id, { enabled: v })} data-testid={`cla-enabled-${r.entity_id}`} />
                  </div>
                  <Input type="number" className="h-8 sm:w-24 text-sm" value={r.teaching}
                    disabled={!r.enabled} onChange={e => setRow(r.entity_id, { teaching: e.target.value })} data-testid={`cla-teaching-${r.entity_id}`} />
                  <Input type="number" className="h-8 sm:w-24 text-sm" value={r.non_teaching}
                    disabled={!r.enabled} onChange={e => setRow(r.entity_id, { non_teaching: e.target.value })} data-testid={`cla-nonteaching-${r.entity_id}`} />
                  <Button size="sm" title="Save" aria-label="Save CLA" onClick={() => save(r)} disabled={savingId === r.entity_id} className="sm:w-16" data-testid={`cla-save-${r.entity_id}`}>
                    {savingId === r.entity_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
